import React, { useState, useEffect } from "react";
import {
  Container,
  Typography,
  Box,
  TextField,
  Button,
  Paper,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemButton,
  CircularProgress,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon as MenuItemIcon,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper as TablePaper,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Tooltip,
  Select,
  FormControl,
  ButtonGroup,
} from "@mui/material";
import {
  FolderPlus,
  Folder,
  Search,
  MoreVertical,
  Edit,
  Trash2,
  Eye,
  Download,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
  Maximize2,
} from "lucide-react";
import AddProjectModal from "./AddProjectModal";
import EditProjectModal from "./EditProjectModal";
import { toast } from "react-toastify";
import generalApi from "../../api/generalApi";
import ConfirmationModal from "../common/ConfirmationModal";
import { getFlagInfo, formatLanguageDisplay } from "../../utils/flagUtils";
import ReactCountryFlag from "react-country-flag";
import ItemEditorModal from "./ItemEditorModal";
import ImagePreviewModal from "./ImagePreviewModal";

const formatDate = (dateString) => {
  const date = new Date(dateString);
  return date.toLocaleDateString("en-US", {
    year: "numeric",
    month: "short",
    day: "2-digit",
  });
};

const ProjectsScreen = () => {
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isItemEditorOpen, setIsItemEditorOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedProject, setSelectedProject] = useState(null);
  const [anchorEl, setAnchorEl] = useState(null);
  const [selectedMenuProject, setSelectedMenuProject] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [projectToDelete, setProjectToDelete] = useState(null);
  const [isItemDeleteModalOpen, setIsItemDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [selectedItem, setSelectedItem] = useState(null);
  const [sortConfig, setSortConfig] = useState({ key: 'updatedAt', direction: 'desc' });
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedImagePreview, setSelectedImagePreview] = useState(null);

  // Fetch projects on component mount
  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await generalApi.project.getProjects();
      setProjects(response.results || []);
      return response.results || [];
    } catch (err) {
      console.error("Error fetching projects:", err);
      setError("Failed to load projects");
      toast.error("Failed to load projects");
      return [];
    } finally {
      setLoading(false);
    }
  };

  const handleProjectAdded = async (newProject) => {
    try {
      setLoading(true);
      // Fetch the updated list of projects
      const response = await generalApi.project.getProjects();
      // Update projects state with the new list
      setProjects(response.results || []);
      
      // Find and select the newly created project in the updated list
      const createdProject = (response.results || []).find(p => p._id === newProject._id);
      if (createdProject) {
        setSelectedProject(createdProject);
      }
      
      // Close the modal
      setIsAddModalOpen(false);
    } catch (error) {
      console.error('Error refreshing projects list:', error);
      toast.error('Failed to refresh projects list');
    } finally {
      setLoading(false);
    }
  };

  const handleProjectUpdated = async (updatedProject) => {
    try {
      await generalApi.project.updateProject(updatedProject);
      setProjects((prevProjects) =>
        prevProjects.map((project) =>
          project._id === updatedProject._id ? updatedProject : project
        )
      );
      toast.success("Project updated successfully!");
      setIsEditModalOpen(false);
      setSelectedProject(null);
    } catch (err) {
      console.error("Error updating project:", err);
      toast.error("Failed to update project");
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
  };

  const filteredProjects = searchTerm
    ? projects.filter((project) => project?.name?.includes(searchTerm))
    : projects;

  const handleMenuOpen = (event, project) => {
    event.stopPropagation(); // Prevent list item click
    setAnchorEl(event.currentTarget);
    setSelectedMenuProject(project);
  };

  const handleMenuClose = (event) => {
    if (event) {
      event.stopPropagation();
    }
    setAnchorEl(null);
    setSelectedMenuProject(null);
  };

  const handleEditClick = (event) => {
    handleMenuClose(event);
    setSelectedProject(selectedMenuProject);
    setIsEditModalOpen(true);
  };

  const handleDeleteClick = (event) => {
    event.stopPropagation();
    handleMenuClose();
    setProjectToDelete(selectedMenuProject);
    setIsDeleteModalOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!projectToDelete) return;

    try {
      await generalApi.project.deleteProject(projectToDelete._id);
      setProjects((prevProjects) =>
        prevProjects.filter((project) => project._id !== projectToDelete._id)
      );
      toast.success("Project deleted successfully!");

      if (selectedProject?._id === projectToDelete._id) {
        setSelectedProject(null);
        setIsEditModalOpen(false);
      }
    } catch (err) {
      console.error("Error deleting project:", err);
      toast.error("Failed to delete project");
    } finally {
      setIsDeleteModalOpen(false);
      setProjectToDelete(null);
      setSelectedMenuProject(null);
    }
  };

  const handleCloseDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setProjectToDelete(null);
  };

  const handleItemEditClick = (item) => {
    setSelectedItem(item);
    setIsItemEditorOpen(true);
  };

  const handleItemUpdated = (updatedItem) => {
    // Update the selected project state with the updated item
    if (selectedProject) {
      const updatedItems = selectedProject.projectItems.map(item => 
        item._id === updatedItem._id ? updatedItem : item
      );
      
      setSelectedProject({
        ...selectedProject,
        projectItems: updatedItems
      });
    }
  };

  const handleItemDeleteClick = (item) => {
    setItemToDelete(item);
    setIsItemDeleteModalOpen(true);
  };

  const handleConfirmItemDelete = async () => {
    if (!itemToDelete || !selectedProject) return;

    setIsDeleting(true);
    try {
      if (itemToDelete.type === "meme") {
        await generalApi.project.deleteMeme(
          selectedProject._id,
          itemToDelete._id
        );

        // Update selectedProject state by removing the deleted meme
        setSelectedProject((prev) => ({
          ...prev,
          meme: (prev.meme || []).filter((m) => m._id !== itemToDelete._id),
        }));
      } else if (itemToDelete.type === "blog") {
        await generalApi.project.deleteArticle(
          selectedProject._id,
          itemToDelete._id
        );

        // Update selectedProject state by removing the deleted blog post
        setSelectedProject((prev) => ({
          ...prev,
          blogPosts: (prev.blogPosts || []).filter(
            (b) => b._id !== itemToDelete._id
          ),
        }));
      } else {
        // Handle deletion of project items
        await generalApi.project.deleteProjectItem(
          selectedProject._id,
          itemToDelete._id
        );

        // Update selectedProject state by removing the deleted project item
        setSelectedProject((prev) => ({
          ...prev,
          projectItems: (prev.projectItems || []).filter(
            (item) => item._id !== itemToDelete._id
          ),
        }));
      }

      toast.success(
        `${
          itemToDelete.type === "meme" 
            ? "Meme" 
            : itemToDelete.type === "blog" 
              ? "Article" 
              : "Item"
        } deleted successfully!`
      );
    } catch (error) {
      console.error("Error deleting item:", error);
      toast.error(
        `Failed to delete ${
          itemToDelete.type === "meme" 
            ? "meme" 
            : itemToDelete.type === "blog" 
              ? "article" 
              : "item"
        }`
      );
    } finally {
      setIsDeleting(false);
      setIsItemDeleteModalOpen(false);
      setItemToDelete(null);
    }
  };

  const handleSort = (key) => {
    setSortConfig(prevConfig => ({
      key,
      direction: prevConfig.key === key && prevConfig.direction === 'asc' ? 'desc' : 'asc'
    }));

    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    const sortedItems = [...selectedProject.projectItems].sort((a, b) => {
      switch(key) {
        case 'name':
          return direction === 'asc' 
            ? a.name.localeCompare(b.name)
            : b.name.localeCompare(a.name);
        case 'type':
          return direction === 'asc'
            ? a.type.localeCompare(b.type)
            : b.type.localeCompare(a.type);
        case 'updatedAt':
          return direction === 'asc'
            ? new Date(a.updatedAt) - new Date(b.updatedAt)
            : new Date(b.updatedAt) - new Date(a.updatedAt);
        default:
          return 0;
      }
    });

    setSelectedProject(prev => ({
      ...prev,
      projectItems: sortedItems
    }));
  };

  const handleMenuSortOpen = (event) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
  };

  const handleMenuSortClose = () => {
    setMenuAnchorEl(null);
  };

  const handleImagePreview = (item) => {
    setSelectedImagePreview(item);
  };

  return (
    <Container 
      maxWidth="xl" 
      sx={{ 
        py: { xs: 2, sm: 4 },
        px: { xs: 1, sm: 2, md: 2 },
        '&.MuiContainer-maxWidthXl': {
          maxWidth: '1800px'
        }
      }}
    >
      <AddProjectModal
        open={isAddModalOpen}
        onClose={() => setIsAddModalOpen(false)}
        onProjectAdded={handleProjectAdded}
      />

      {selectedProject && (
        <EditProjectModal
          open={isEditModalOpen}
          onClose={() => {
            setIsEditModalOpen(false);
            setSelectedProject(null);
          }}
          project={selectedProject}
          onProjectUpdated={handleProjectUpdated}
        />
      )}

      {selectedItem && selectedProject && (
        <ItemEditorModal
          open={isItemEditorOpen}
          onClose={() => {
            setIsItemEditorOpen(false);
            setSelectedItem(null);
          }}
          projectId={selectedProject._id}
          item={selectedItem}
          onItemUpdated={handleItemUpdated}
        />
      )}

      <ConfirmationModal
        open={isDeleteModalOpen}
        onClose={handleCloseDeleteModal}
        onConfirm={handleConfirmDelete}
        title="Delete Project"
        message="Are you sure you want to delete this project? This action cannot be undone."
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
      />

      <ConfirmationModal
        open={isItemDeleteModalOpen}
        onClose={() => {
          setIsItemDeleteModalOpen(false);
          setItemToDelete(null);
        }}
        onConfirm={handleConfirmItemDelete}
        title="Delete Item"
        message={
          <>
            <Typography gutterBottom>
              Are you sure you want to delete this{" "}
              {itemToDelete?.type === "meme" 
                ? "meme" 
                : itemToDelete?.type === "blog" 
                  ? "blog post" 
                  : "item"}?
            </Typography>
            <Box sx={{ mt: 2, p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                Type: {
                  itemToDelete?.type === "meme" 
                    ? "Meme" 
                    : itemToDelete?.type === "blog" 
                      ? "Blog Post" 
                      : itemToDelete?.type || "Content"
                }
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {itemToDelete?.type === "meme"
                  ? `Description: ${itemToDelete?.memeText || "No description"}`
                  : itemToDelete?.type === "blog"
                    ? `Title: ${itemToDelete?.title || "Untitled"}`
                    : `Name: ${itemToDelete?.name || "Unnamed"}`}
              </Typography>
            </Box>
            <Typography sx={{ mt: 2 }} color="error" variant="body2">
              This action cannot be undone!
            </Typography>
          </>
        }
        confirmText="Delete"
        cancelText="Cancel"
        severity="error"
        loading={isDeleting}
        disabled={isDeleting}
      />

      <Box sx={{ 
        display: "flex",
        flexDirection: { xs: "column", md: "row" },
        height: { xs: "auto", md: "calc(100vh - 150px)" },
        gap: { xs: 2, md: 0 },
        minHeight: 0
      }}>
        {/* Sidebar */}
        <Paper
          sx={{
            width: { xs: "100%", md: "280px" },
            minWidth: { md: "280px" },
            p: 2,
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
            borderRadius: { xs: "4px", md: "4px 0 0 4px" },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider",
          }}
        >
          <Typography
            variant="h5"
            component="h1"
            sx={{ mb: 4, fontWeight: "bold" }}
          >
            Projects
          </Typography>

          <TextField
            fullWidth
            placeholder="Search projects..."
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: (
                <Search size={20} style={{ marginRight: 8, color: "#666" }} />
              ),
            }}
            sx={{ mb: 2 }}
          />

          <Button
            variant="contained"
            startIcon={<FolderPlus />}
            fullWidth
            onClick={() => setIsAddModalOpen(true)}
            sx={{
              bgcolor: "#9c27b0",
              "&:hover": { bgcolor: "#7b1fa2" },
              textTransform: "none",
              mb: 2,
            }}
          >
            New Project
          </Button>

          <Divider sx={{ mb: 2 }} />

          {/* Project List */}
          <List sx={{ 
            flexGrow: 1, 
            overflow: "auto",
            maxHeight: { xs: '40vh', md: 'none' }
          }}>
            {loading ? (
              <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
                <CircularProgress />
              </Box>
            ) : error ? (
              <Typography color="error" align="center" sx={{ p: 2 }}>
                {error}
              </Typography>
            ) : filteredProjects.length === 0 ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                  height: "100%",
                  color: "text.secondary",
                  textAlign: "center",
                  p: 2,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                }}
              >
                <Folder size={48} style={{ marginBottom: 16, opacity: 0.5 }} />
                <Typography variant="body2">
                  {searchTerm
                    ? "No matching projects found"
                    : "No projects yet. Create your first project to get started!"}
                </Typography>
              </Box>
            ) : (
              filteredProjects.map((project) => (
                <ListItem
                  key={project._id}
                  disablePadding
                  secondaryAction={
                    <IconButton
                      edge="end"
                      aria-label="more"
                      onClick={(e) => handleMenuOpen(e, project)}
                      sx={{ mr: 1 }}
                    >
                      <MoreVertical size={20} />
                    </IconButton>
                  }
                >
                  <ListItemButton
                    onClick={() => handleProjectSelect(project)}
                    selected={selectedProject?._id === project._id}
                  >
                    <ListItemIcon sx={{ minWidth: 36 }}>
                      <Folder size={20} />
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box
                          sx={{ display: "flex", alignItems: "center", gap: 1 }}
                        >
                          <ReactCountryFlag
                            countryCode={
                              getFlagInfo(project.language).countryCode
                            }
                            svg
                            style={{
                              width: "1.5em",
                              height: "1.5em",
                            }}
                          />
                          <Typography
                            sx={{
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                              whiteSpace: "nowrap",
                              maxWidth: "180px", // Adjust this value based on your needs
                            }}
                          >
                            {project.name}
                          </Typography>
                        </Box>
                      }
                      secondary={project.description}
                      secondaryTypographyProps={{
                        sx: {
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: "1",
                          WebkitBoxOrient: "vertical",
                          lineHeight: "1.4em",
                          height: "1.4em",
                        },
                      }}
                    />
                  </ListItemButton>
                </ListItem>
              ))
            )}

            {/* More Options Menu */}
            <Menu
              anchorEl={anchorEl}
              open={Boolean(anchorEl)}
              onClose={handleMenuClose}
              onClick={handleMenuClose}
              PaperProps={{
                elevation: 0,
                sx: {
                  overflow: "visible",
                  filter: "drop-shadow(0px 2px 8px rgba(0,0,0,0.12))",
                  mt: 1,
                  "& .MuiMenuItem-root": {
                    px: 2,
                    py: 1,
                    gap: 1.5,
                  },
                },
              }}
              transformOrigin={{ horizontal: "right", vertical: "top" }}
              anchorOrigin={{ horizontal: "right", vertical: "bottom" }}
            >
              {[
                {
                  id: "edit",
                  label: "Edit",
                  icon: <Edit size={18} />,
                  onClick: handleEditClick,
                  color: "inherit",
                },
                {
                  id: "delete",
                  label: "Delete",
                  icon: <Trash2 size={18} />,
                  onClick: handleDeleteClick,
                  color: "error.main",
                },
              ].map((item) => (
                <MenuItem
                  key={item.id}
                  onClick={item.onClick}
                  sx={{ color: item.color }}
                >
                  <MenuItemIcon>{item.icon}</MenuItemIcon>
                  {item.label}
                </MenuItem>
              ))}
            </Menu>
          </List>
        </Paper>

        {/* Main Content */}
        <Paper
          sx={{
            flexGrow: 1,
            width: { md: 'calc(100% - 280px)' },
            p: { xs: 2, sm: 3, md: 4 },
            height: { xs: "auto", md: "100%" },
            display: "flex",
            flexDirection: "column",
            borderRadius: { xs: "4px", md: "0 4px 4px 0" },
            boxShadow: "none",
            border: "1px solid",
            borderColor: "divider",
            overflow: "hidden"
          }}
        >
          {selectedProject ? (
            <Box sx={{ 
              height: "100%", 
              overflow: "auto",
              width: "100%"
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 3 }}>
                <Typography variant="h5" sx={{ color: 'white', fontWeight: 600 }}>
                  {selectedProject?.name}
                </Typography>
                <Box sx={{ 
                  display: 'flex', 
                  gap: 1,
                  bgcolor: '#2a2a2a',
                  p: 0.5,
                  borderRadius: '8px',
                }}>
                  <Button
                    onClick={() => handleSort('name')}
                    startIcon={sortConfig.key === 'name' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />
                    ) : null}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      bgcolor: sortConfig.key === 'name' ? '#404040' : 'transparent',
                      '&:hover': {
                        bgcolor: sortConfig.key === 'name' ? '#454545' : 'rgba(255, 255, 255, 0.1)',
                      },
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    Name
                  </Button>
                  <Button
                    onClick={() => handleSort('type')}
                    startIcon={sortConfig.key === 'type' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />
                    ) : null}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      bgcolor: sortConfig.key === 'type' ? '#404040' : 'transparent',
                      '&:hover': {
                        bgcolor: sortConfig.key === 'type' ? '#454545' : 'rgba(255, 255, 255, 0.1)',
                      },
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    Type
                  </Button>
                  <Button
                    onClick={() => handleSort('updatedAt')}
                    startIcon={sortConfig.key === 'updatedAt' ? (
                      sortConfig.direction === 'asc' ? <ArrowUp size={16} /> : <ArrowDown size={16} />
                    ) : null}
                    sx={{
                      color: 'rgba(255, 255, 255, 0.9)',
                      bgcolor: sortConfig.key === 'updatedAt' ? '#404040' : 'transparent',
                      '&:hover': {
                        bgcolor: sortConfig.key === 'updatedAt' ? '#454545' : 'rgba(255, 255, 255, 0.1)',
                      },
                      textTransform: 'none',
                      px: 2,
                      py: 0.75,
                      borderRadius: '6px',
                      fontSize: '0.875rem',
                      fontWeight: 500,
                    }}
                  >
                    Last Updated
                  </Button>
                </Box>
              </Box>

              <TableContainer
                component={TablePaper}
                sx={{
                  boxShadow: "none",
                  border: "1px solid",
                  borderColor: "divider",
                  width: "100%",
                  overflow: "auto",
                  maxHeight: { xs: "calc(100vh - 300px)", md: "none" },
                  display: { xs: 'none', md: 'none' }  // Hide table view completely
                }}
              >
                <Table
                  sx={{ 
                    minWidth: { xs: "100%", sm: 650 },
                    tableLayout: "fixed",
                    "& .MuiTableCell-root": {
                      px: { xs: 1, sm: 2 },
                      py: { xs: 1.5, sm: 2 },
                      whiteSpace: "normal",
                      wordBreak: "break-word"
                    }
                  }}
                  aria-label="project content table"
                >
                  <TableHead>
                    <TableRow sx={{ backgroundColor: "background.default" }}>
                      <TableCell sx={{ width: { xs: '40%', sm: '250px' } }}>Name</TableCell>
                      <TableCell sx={{ width: { xs: '30%', sm: 'auto' } }}>Content Type</TableCell>
                      <TableCell sx={{ 
                        display: { xs: 'none', sm: 'table-cell' },
                        width: { sm: '300px' }
                      }}>Content Preview</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Created</TableCell>
                      <TableCell sx={{ display: { xs: 'none', sm: 'table-cell' } }}>Updated</TableCell>
                      <TableCell align="right" sx={{ width: { xs: '30%', sm: 'auto' } }}>Actions</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {/* Combine and sort memes, blog posts, and project items */}
                    {[
                      ...(selectedProject.meme || []).map((item) => ({
                        ...item,
                        type: "meme",
                      })),
                      ...(selectedProject.blogPosts || []).map((item) => ({
                        ...item,
                        type: "blog",
                      })),
                      ...(selectedProject.projectItems || []).map((item) => ({
                        ...item,
                        type: item.type || "content",
                      })),
                    ].sort(
                      (a, b) => new Date(b.createdAt) - new Date(a.createdAt)
                    ).length > 0 ? (
                      <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: '1fr',
                          sm: '1fr',
                          md: '1fr'
                        },
                        gap: { xs: 2, sm: 2.5 },
                        maxWidth: '100%'
                      }}>
                        {[
                          ...(selectedProject.meme || []).map((item) => ({
                            ...item,
                            type: "meme",
                          })),
                          ...(selectedProject.blogPosts || []).map((item) => ({
                            ...item,
                            type: "blog",
                          })),
                          ...(selectedProject.projectItems || []).map((item) => ({
                            ...item,
                            type: item.type || "content",
                          })),
                        ]
                          .sort(
                            (a, b) =>
                              new Date(b.createdAt) - new Date(a.createdAt)
                          )
                          .map((item, index, items) => (  // Added items parameter to map
                            <Paper
                              key={item._id}
                              elevation={0}
                              sx={{
                                p: { xs: 2, sm: 2.5 },
                                border: "1px solid",
                                borderColor: "divider",
                                borderRadius: 2,
                                bgcolor: 'background.paper',
                                height: '100%',
                                display: 'flex',
                                flexDirection: 'column',
                                transition: 'all 0.2s ease-in-out',
                                position: 'relative',
                                '&:hover': {
                                  transform: 'translateY(-4px)',
                                  borderColor: 'primary.main',
                                  boxShadow: (theme) => `
                                    0 4px 10px -2px ${theme.palette.primary.main}20,
                                    0 6px 20px -4px ${theme.palette.primary.main}15
                                  `,
                                  '& .action-buttons': {
                                    opacity: 1,
                                    transform: 'translateY(0)',
                                  },
                                  '&:after': {
                                    opacity: 1,
                                  }
                                },
                                '&:after': {
                                  content: '""',
                                  position: 'absolute',
                                  top: 0,
                                  left: 0,
                                  right: 0,
                                  bottom: 0,
                                  borderRadius: 2,
                                  background: (theme) => `linear-gradient(145deg, 
                                    ${theme.palette.primary.main}05,
                                    ${theme.palette.primary.main}02,
                                    ${theme.palette.primary.main}08
                                  )`,
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease-in-out',
                                  pointerEvents: 'none',
                                },
                                ...(items.length === 1 && {
                                  maxHeight: { sm: '400px' },
                                  mx: 'auto',
                                  width: '100%'
                                })
                              }}
                            >
                              <Box sx={{ 
                                display: 'flex', 
                                flexDirection: 'column',
                                height: '100%',
                                position: 'relative',
                                zIndex: 1
                              }}>
                                <Box sx={{ 
                                  display: 'flex', 
                                  alignItems: 'flex-start',
                                  justifyContent: 'space-between',
                                  gap: 2,
                                  mb: 1.5
                                }}>
                                  <Box sx={{ flex: 1, minWidth: 0 }}>
                                    {item.type === "blog" ? (
                                      <Link
                                        href={`/social-first-blog-posts/${item._id}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                          textDecoration: "none",
                                          color: "primary.main",
                                          fontWeight: 600,
                                          fontSize: { xs: '1.1rem', sm: '1.1rem' },
                                          display: 'block',
                                          mb: 1
                                        }}
                                      >
                                        {item.title || "Untitled"}
                                      </Link>
                                    ) : (
                                      <Typography
                                        sx={{
                                          fontWeight: 600,
                                          fontSize: { xs: '1.1rem', sm: '1.1rem' },
                                          mb: 1,
                                          color: 'text.primary'
                                        }}
                                      >
                                        {item.name || item.memeText || "-"}
                                      </Typography>
                                    )}
                                    
                                    <Box sx={{ 
                                      display: 'flex', 
                                      alignItems: 'center',
                                      gap: 1.5,
                                      flexWrap: 'wrap'
                                    }}>
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          color: 'primary.main',
                                          bgcolor: 'primary.50',
                                          px: 1.5,
                                          py: 0.75,
                                          borderRadius: 1.5,
                                          fontSize: '0.8rem',
                                          fontWeight: 500
                                        }}
                                      >
                                        {item.type === "meme" 
                                          ? "Meme" 
                                          : item.type === "blog" 
                                            ? "Blog Post" 
                                            : item.type}
                                      </Typography>
                                      <Typography 
                                        variant="caption" 
                                        color="text.secondary"
                                        sx={{
                                          fontSize: '0.8rem'
                                        }}
                                      >
                                        Updated {formatDate(item.updatedAt || item.createdAt || selectedProject.createdAt)}
                                      </Typography>
                                    </Box>
                                  </Box>

                                  <Box sx={{ 
                                    display: 'flex',
                                    gap: 0.5,
                                    flexShrink: 0,
                                    opacity: { xs: 1, sm: 0.4 },
                                    transform: { xs: 'none', sm: 'translateY(4px)' },
                                    transition: 'all 0.2s ease-in-out',
                                    className: 'action-buttons'
                                  }}>
                                    {item.type !== "meme" && item.type !== "blog" && item.type !== "Image" && (
                                      <IconButton
                                        onClick={() => handleItemEditClick(item)}
                                        size="small"
                                        sx={{ p: { xs: 1, sm: 0.75 } }}
                                        title={`Edit ${item.type === "meme" ? "meme" : item.type === "blog" ? "blog post" : "item"}`}
                                      >
                                        <Edit size={18} />
                                      </IconButton>
                                    )}
                                    <IconButton
                                      onClick={() => handleItemDeleteClick(item)}
                                      size="small"
                                      sx={{ 
                                        color: "error.main",
                                        p: { xs: 1, sm: 0.75 }
                                      }}
                                      title={`Delete ${item.type === "meme" ? "meme" : item.type === "blog" ? "blog post" : "item"}`}
                                    >
                                      <Trash2 size={18} />
                                    </IconButton>
                                  </Box>
                                </Box>

                                {item.type === "meme" ? (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: { xs: 200, sm: 180 },
                                      position: "relative",
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      bgcolor: "background.paper",
                                      mt: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      flexGrow: 1
                                    }}
                                  >
                                    {item.url ? (
                                      <img
                                        src={item.url}
                                        alt={item.description}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <Box
                                        sx={{
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                          height: "100%",
                                        }}
                                      >
                                        <ImageIcon size={24} />
                                      </Box>
                                    )}
                                  </Box>
                                ) : item.type === "Image" ? (
                                  <Box
                                    sx={{
                                      width: "100%",
                                      height: { xs: 200, sm: 180 },
                                      position: "relative",
                                      borderRadius: 2,
                                      overflow: "hidden",
                                      bgcolor: "background.paper",
                                      mt: 2,
                                      border: '1px solid',
                                      borderColor: 'divider',
                                      flexGrow: 1
                                    }}
                                  >
                                    <img
                                      src={item.content}
                                      alt={item.name}
                                      style={{
                                        width: "100%",
                                        height: "100%",
                                        objectFit: "cover",
                                      }}
                                    />
                                  </Box>
                                ) : (
                                  item.content && (
                                    <Typography
                                      variant="body2"
                                      sx={{
                                        color: "text.secondary",
                                        mt: 2,
                                        whiteSpace: 'nowrap',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        width: '100%'
                                      }}
                                    >
                                      {item.content.replace(/<[^>]*>/g, '')}
                                    </Typography>
                                  )
                                )}
                              </Box>
                            </Paper>
                          ))
                        }
                      </Box>
                    ) : (
                      <TableRow>
                        <TableCell colSpan={6} align="center" sx={{ py: 4 }}>
                          <Box
                            sx={{
                              color: "text.secondary",
                              textAlign: "center",
                            }}
                          >
                            <Folder
                              size={32}
                              style={{ marginBottom: 8, opacity: 0.5, marginTop: 8 }}
                            />
                            <Typography variant="body2">
                              No content has been added to this project yet.
                            </Typography>
                          </Box>
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </TableContainer>

              {/* Card View for All Screen Sizes */}
              <Box sx={{ 
                display: 'grid', 
                gap: 2, 
                width: '100%',
                overflow: 'hidden'
              }}>
                {selectedProject.projectItems.map((item) => (
                  <Paper
                    key={item._id}
                    elevation={1}
                    sx={{
                      p: 2.5,
                      bgcolor: '#2a2a2a',
                      borderRadius: 2,
                      transition: 'all 0.2s ease-in-out',
                      border: '1px solid rgba(255, 255, 255, 0.1)',
                      overflow: 'hidden',
                      '&:hover': {
                        bgcolor: '#333333',
                        transform: 'translateY(-1px)',
                        boxShadow: '0 4px 20px -5px rgba(0, 0, 0, 0.2)',
                      }
                    }}
                  >
                    {/* Top Section - Name, Type, and Actions */}
                    <Box sx={{ 
                      display: 'flex',
                      justifyContent: 'space-between',
                      alignItems: 'flex-start',
                      mb: 2,
                      minWidth: 0,
                      width: '100%'
                    }}>
                      {/* Name and Type */}
                      <Box sx={{ 
                        minWidth: 0,
                        flex: 1
                      }}>
                        <Typography 
                          variant="subtitle1" 
                          sx={{ 
                            color: 'white',
                            fontSize: '0.9rem',
                            fontWeight: 600,
                            mb: 0.5
                          }}
                        >
                          {item.name}
                        </Typography>
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.5)',
                            fontSize: '0.8rem',
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          {item.type || 'Content'}
                        </Typography>
                      </Box>

                      {/* Action Buttons */}
                      <Box sx={{ display: 'flex', gap: 0.5 }}>
                        {item.type === "Image" && (
                          <Tooltip title="View image" arrow placement="top">
                            <IconButton
                              onClick={() => handleImagePreview(item)}
                              size="small"
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': { 
                                  color: 'white',
                                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                                }
                              }}
                            >
                              <Maximize2 size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        {item.type !== "Image" && (
                          <Tooltip title="Edit item" arrow placement="top">
                            <IconButton
                              onClick={() => handleItemEditClick(item)}
                              size="small"
                              sx={{ 
                                color: 'rgba(255, 255, 255, 0.7)',
                                '&:hover': { 
                                  color: 'white',
                                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                                }
                              }}
                            >
                              <Edit size={18} />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Delete item" arrow placement="top">
                          <IconButton
                            onClick={() => handleItemDeleteClick(item)}
                            size="small"
                            sx={{ 
                              color: 'rgba(255, 255, 255, 0.7)',
                              '&:hover': { 
                                color: '#ff4444',
                                bgcolor: 'rgba(255, 68, 68, 0.1)'
                              }
                            }}
                          >
                            <Trash2 size={18} />
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </Box>

                    {/* Content Preview */}
                    <Box sx={{ 
                      width: '100%',
                      overflow: 'hidden'
                    }}>
                      {item.type === "Image" ? (
                        <Box
                          onClick={() => handleImagePreview(item)}
                          sx={{
                            width: '100%',
                            height: 200,
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            position: 'relative',
                            '&:hover': {
                              '&::after': {
                                content: '""',
                                position: 'absolute',
                                top: 0,
                                left: 0,
                                right: 0,
                                bottom: 0,
                                background: 'rgba(0, 0, 0, 0.3)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                              },
                              '& .preview-icon': {
                                opacity: 1,
                                transform: 'translate(-50%, -50%) scale(1)',
                              }
                            }
                          }}
                        >
                          <img
                            src={item.content}
                            alt={item.name}
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'cover',
                            }}
                          />
                          <Maximize2
                            className="preview-icon"
                            size={24}
                            style={{
                              position: 'absolute',
                              top: '50%',
                              left: '50%',
                              transform: 'translate(-50%, -50%) scale(0.8)',
                              color: 'white',
                              opacity: 0,
                              transition: 'all 0.2s ease-in-out',
                              zIndex: 1,
                            }}
                          />
                        </Box>
                      ) : (
                        <Typography 
                          variant="body1" 
                          sx={{ 
                            color: 'rgba(255, 255, 255, 0.9)',
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                            fontSize: '0.95rem',
                            minWidth: 0
                          }}
                        >
                          {item.content ? item.content.replace(/<[^>]*>/g, '') : ''}
                        </Typography>
                      )}
                    </Box>
                  </Paper>
                ))}
              </Box>
            </Box>
          ) : (
            <Box
              sx={{
                flexGrow: 1,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                textAlign: "center",
                color: "text.secondary",
                minHeight: { xs: "200px", md: "auto" }
              }}
            >
              <Box>
                <Folder size={64} style={{ marginBottom: 16, opacity: 0.5 }} />
                <Typography variant="h6">
                  Select a project to view details
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      <ImagePreviewModal
        open={!!selectedImagePreview}
        onClose={() => setSelectedImagePreview(null)}
        imageUrl={selectedImagePreview?.content}
        imageName={selectedImagePreview?.name}
      />
    </Container>
  );
};

export default ProjectsScreen;
